import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import VideoCard from "@/components/base/VideoCard";
import EmptyState from "@/components/base/EmptyState";
import LoadingGrid from "@/components/base/LoadingGrid";
import ErrorState from "@/components/base/ErrorState";
import Pagination from "@/components/base/Pagination";
import FilterBar, { type CategoryFilters } from "@/pages/category/components/FilterBar";
import { useAsyncData } from "@/hooks/useAsyncData";
import { usePageMeta } from "@/hooks/usePageMeta";
import {
  getCategoryById,
  getVideosByCategory,
  getCategoryTypes,
  getAllTags,
  regions,
  years,
} from "@/utils/video";
import type { Video } from "@/types";

const PAGE_SIZE = 6;

const defaultFilters: CategoryFilters = {
  type: "全部",
  region: "全部",
  year: "全部",
  tags: [],
  sort: "latest",
};

function parseViews(views: string): number {
  if (views.includes("亿")) return parseFloat(views) * 100000000;
  if (views.includes("万")) return parseFloat(views) * 10000;
  return parseFloat(views) || 0;
}

export default function CategoryPage() {
  const { categoryId = "" } = useParams();
  const category = getCategoryById(categoryId);

  const [filters, setFilters] = useState<CategoryFilters>(defaultFilters);
  const [page, setPage] = useState(1);

  const { data, loading, error, retry } = useAsyncData<Video[]>(
    () => getVideosByCategory(categoryId),
    [categoryId],
  );

  useEffect(() => {
    setFilters(defaultFilters);
    setPage(1);
  }, [categoryId]);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  usePageMeta(
    category ? `${category.name} - 高清免费视频 - 在线观看` : "分类 - 精品视频",
    category
      ? `${category.name}分类汇集大量高清免费视频资源，${category.description}`
      : "精品视频分类页，汇集高清免费视频资源。",
  );

  const filtered = useMemo(() => {
    const list = (data ?? []).filter((v) => {
      if (filters.type !== "全部" && v.type !== filters.type) return false;
      if (filters.region !== "全部" && v.region !== filters.region) return false;
      if (filters.year !== "全部" && String(v.year) !== filters.year) return false;
      if (filters.tags.length > 0 && !filters.tags.every((t) => v.tags.includes(t))) return false;
      return true;
    });

    return [...list].sort((a, b) => {
      if (filters.sort === "hot") return parseViews(b.views) - parseViews(a.views);
      if (filters.sort === "rating") return Number(b.rating) - Number(a.rating);
      return new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime();
    });
  }, [data, filters]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  if (!category) {
    return (
      <div className="w-full px-4 md:px-8 pt-24 md:pt-28 pb-16">
        <EmptyState
          icon="ri-error-warning-line"
          title="分类不存在"
          description="你访问的分类可能已被移除，去首页看看其他内容吧。"
          actionLabel="返回首页"
          actionTo="/"
        />
      </div>
    );
  }

  return (
    <div className="w-full px-4 md:px-8 pt-24 md:pt-28 pb-16 md:pb-20">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: category.name }]} />

      <div className="mt-4 flex items-start gap-4">
        <span className="flex items-center justify-center w-14 h-14 rounded-lg bg-primary-100 text-primary-600 shrink-0">
          <i className={`${category.icon} text-3xl`} />
        </span>
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-foreground-950">{category.name}</h1>
          <p className="mt-1.5 text-sm text-foreground-500 max-w-2xl leading-relaxed">
            {category.description}
          </p>
        </div>
      </div>

      <div className="mt-6">
        <FilterBar
          options={{
            types: getCategoryTypes(category.id),
            regions,
            years,
            tags: getAllTags(category.id),
          }}
          value={filters}
          onChange={setFilters}
          onReset={() => setFilters(defaultFilters)}
        />
      </div>

      <div className="mt-6 flex items-center justify-between">
        <p className="text-sm text-foreground-500">
          共找到 <span className="font-semibold text-primary-600">{filtered.length}</span> 部内容
        </p>
      </div>

      <div className="mt-4">
        {error ? (
          <ErrorState onRetry={retry} />
        ) : loading ? (
          <LoadingGrid count={PAGE_SIZE} />
        ) : pageItems.length === 0 ? (
          <EmptyState
            icon="ri-filter-off-line"
            title="没有符合筛选条件的视频"
            description="试试放宽筛选条件，或点击下方按钮重置全部筛选。"
            actionLabel="重置筛选"
            onAction={() => setFilters(defaultFilters)}
          />
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
              {pageItems.map((v, i) => (
                <VideoCard key={v.id} video={v} index={i} />
              ))}
            </div>
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </>
        )}
      </div>
    </div>
  );
}